import { klona } from 'klona'
import type { InferGetServerSidePropsType } from 'next'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'
import React, { useMemo } from 'react'
import { AlertPopup } from '../../app/components/AlertPopup'
import { AuthTest } from '../../app/components/AuthTest'
import { Box } from '../../app/components/Box'
import { Button } from '../../app/components/Buttons'
import { ChangePasswordForm } from '../../app/components/ChangePasswordForm'
import { Container } from '../../app/components/Container'
import { CustomUpload } from '../../app/components/CustomUpload'
import { Flex } from '../../app/components/Flex'
import { Footer } from '../../app/components/Footer'
import { Input } from '../../app/components/Input'
import { Nav } from '../../app/components/Nav'
import { PageInfo } from '../../app/components/PageInfo'
import { TextArea } from '../../app/components/TextArea'
import { scalarResolver } from '../../app/data/createScalarResolvers'
import { adminZeus } from '../../app/data/thunder'
import { useIsSignedIn, useIsSignedOut } from '../../app/hooks/useAuth'
import { usePlanteeUser } from '../../app/hooks/usePlanteeUser'
import styles from '../../app/styles/user.module.sass'
import { serverPropsHandler } from '../../libs/next/api/handlers'

export default function User(_props: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const plantee = usePlanteeUser()
	const user = plantee.auth.me.data?.user

	const signedIn = useIsSignedIn()
	const signedOut = useIsSignedOut()

	const locale = useRouter().locale

	const [avatarUploadPopupOpen, setAvatarUploadPopupOpen] = React.useState<boolean>(false)

	const serverState = plantee.auth.me.data?.user

	const [userInfo, setUserInfo] = React.useState<{ bio: string; nick: string; email: string }>({
		bio: serverState?.label ?? '',
		nick: serverState?.nick ?? '',
		email: serverState?.email ?? '',
	})

	React.useEffect(() => {
		setUserInfo({
			bio: serverState?.label ?? '',
			nick: serverState?.nick ?? '',
			email: serverState?.email ?? '',
		})
	}, [serverState?.email, serverState?.label, serverState?.nick])

	const isDirty = useMemo(() => {
		const changedValues = [
			(serverState?.email ?? '') !== userInfo.email,
			(serverState?.label ?? '') !== userInfo.bio,
			(serverState?.nick ?? '') !== userInfo.nick,
		]

		return changedValues.some(Boolean)
	}, [
		serverState?.email,
		serverState?.label,
		serverState?.nick,
		userInfo.bio,
		userInfo.email,
		userInfo.nick,
	])

	return (
		<div>
			<PageInfo title="Edit my profile" />
			<Nav />
			<Container marginTop={100} view="slim">
				{signedIn && (
					<>
						<AlertPopup
							type="default"
							isOpen={avatarUploadPopupOpen}
							onClickClose={() => {
								setAvatarUploadPopupOpen(false)
							}}>
							<CustomUpload
								accept={{
									'image/*': [],
								}}
								maxSize={10000000}
								iconDecoration="imageIllustration"
								areaDescription="Drop your image here"
								footerDescription={<>Supported files: jpg, png. Max 10 MB.</>}
								onDropAccepted={async ([file]) => {
									console.log(file)
									if (file) {
										await plantee.auth.setAvatar.mutate({
											image: file,
											mimeType: file.type,
										})
										setAvatarUploadPopupOpen(false)
									}
								}}
							/>
						</AlertPopup>
						<form
							onSubmit={(e) => {
								e.preventDefault()
								plantee.auth.setUserInfo.mutate({
									bio: userInfo.bio,
									nick: userInfo.nick,
									email: userInfo.email,
								})
							}}>
							<Flex justifyContent="space-between" marginBottom={20}>
								<Button type="link" href="/" icon={{ name: 'reject', position: 'end' }} isBorder>
									{locale === 'cs' ? 'Zavřít' : 'Close'}
								</Button>
								<Button type="submit" fill="green600" disabled={!isDirty || !userInfo.nick}>
									{locale === 'cs' ? 'Uložit změny' : 'Save changes'}
								</Button>
							</Flex>
							<Flex itemsPerRow={2} mobileItemsPerRow={1} spacing={20}>
								{/* <Dump data={userInfo} />
								<Dump data={plantee.auth.me} /> */}
								<Box view="half">
									<h2>{locale === 'cs' ? 'Profilový obrázek' : 'Profile picture'}</h2>
									<div className={styles.avatar}>
										{user?.avatar && (
											<Image
												key={String(user?.avatar?.id)}
												src={user?.avatar?.url}
												layout="fill"
												objectFit="cover"
												alt={user?.avatar.alt ?? user?.avatar.description ?? ''}
												placeholder="blur"
												blurDataURL={user?.avatar.url}
											/>
										)}
										<button
											className={styles.avatarEdit}
											onClick={() => {
												setAvatarUploadPopupOpen(true)
											}}>
											{locale === 'cs' ? 'Upravit' : 'Edit'}
										</button>
									</div>
									<h2>{locale === 'cs' ? 'O mně' : 'About me'}</h2>
									<TextArea
										placeholder="Bio"
										iconDecoration="pencil"
										value={userInfo.bio}
										onChange={(e) => {
											const newUserInfo = klona(userInfo)
											newUserInfo.bio = e.target.value
											setUserInfo(newUserInfo)
										}}
									/>
								</Box>
								<Box view="half">
									<h2>{locale === 'cs' ? 'Jméno a e-mail' : 'Name & e-mail'}</h2>

									<Input
										id="username"
										name="username"
										type="text"
										iconDecoration="pencil"
										value={userInfo.nick}
										label={{
											isLabel: true,
											icon: 'user',
											text: locale === 'cs' ? 'Přezdívka' : 'Nickname',
										}}
										onChange={(e) => {
											const newUserInfo = klona(userInfo)
											newUserInfo.nick = e.currentTarget.value
											setUserInfo(newUserInfo)
										}}
										placeholder={'Insert your new nickname here'}
									/>
									<p>
										If you want to change your e-mail address, contact support at{' '}
										<a href="mailto:support@plant.ee">support@plant.ee</a>
									</p>
								</Box>
							</Flex>
						</form>
						<Flex itemsPerRow={1} marginTop={20}>
							<Box>
								<h2>{locale === 'cs' ? 'Heslo' : 'Password'}</h2>
								<ChangePasswordForm />
							</Box>
						</Flex>
					</>
				)}
				{signedOut && (
					<Box>
						<AuthTest />
					</Box>
				)}
			</Container>
			<Footer />
		</div>
	)
}

export const getServerSideProps = serverPropsHandler(async () => {
	const data = await adminZeus('query', { scalars: scalarResolver })({
		listPageLocale: [{}, { title: true }],
	})

	return { props: { data } }
})
